/**
 * 화면 안의 질문 캐시.
 *
 * 랭킹은 일별 스냅샷이라 같은 질문이면 결과가 같다. 비교 카드가 여러 장 뜨거나(카드
 * 1장당 3콜) 카드가 리마운트될 때마다 같은 요청을 다시 보내지 않게 질문 단위로 캐시하고,
 * **진행 중인 요청은 promise를 공유**해 중복 호출을 막는다.
 *
 * ## ⚠️ `actions/els`의 2초 캐시로는 못 대신한다
 *
 * 카드 리마운트는 몇 분 뒤일 수도 있다. 그래서 여기는 **TTL이 없고** 수동 무효화로만
 * 버린다 — 상세 수집 직후처럼 값이 바뀐 것을 아는 자리에서만 버리기 때문이다.
 *
 * ## ⚠️ 키는 URL이 아니라 **질문 이름 + 파라미터**다
 *
 * 화면은 URL을 만들지 않는다. 키를 만드는 자리는 `cacheKey` 하나뿐이라 갈릴 데가 없다.
 */

const requestCache = new Map<string, Promise<unknown>>();

/** 이 수를 넘으면 통째로 비운다 — LRU가 아니라 단순 상한이다 */
const REQUEST_CACHE_MAX = 400;

/** ⚠️ **파라미터 객체의 키 순서가 곧 캐시 키다.** 같은 값이라도 순서가 다르면 따로 캐시된다 */
export function cacheKey(name: string, params?: unknown): string {
  return `${name}${JSON.stringify(params ?? {})}`;
}

export function runCached<T>(key: string, run: () => Promise<T>): Promise<T> {
  const hit = requestCache.get(key) as Promise<T> | undefined;
  if (hit) return hit;

  const promise = run();

  // 실패는 캐시에 남기지 않는다 — 다음 시도에서 다시 부를 수 있어야 한다
  promise.catch(() => requestCache.delete(key));

  if (requestCache.size >= REQUEST_CACHE_MAX) requestCache.clear();
  requestCache.set(key, promise);
  return promise;
}

export function invalidateCache(key: string): void {
  requestCache.delete(key);
}
